import * as React from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useSession, useSessionIdentityKey } from "@authe/ui-core/session";
import { Button } from "@authe/ui-core";
import { PageHeader } from "@/components/PageHeader";
import { ROUTES } from "@/config";
import { GettingStarted, QuickStart } from "./onboarding/GettingStarted";
import { useLandingAccess } from "./onboarding/useLandingAccess";

// Queue data loads after the first paint. Transaction tables, graph engines,
// live feeds and setup inventory probes do not belong on the entry path.
const Queue = React.lazy(() => import("./queue/QueuePanels").then((module) => ({
  default: () => <><module.FiredAlertsLane /><module.ActiveCasesLane /><module.LiveWatchesLane /></>,
})));

export function OverviewPage(): React.ReactElement {
  const { session } = useSession();
  const identity = useSessionIdentityKey();
  const navigate = useNavigate();
  const { guide } = useSearch({ strict: false }) as { guide?: boolean };
  const access = useLandingAccess();
  const name = (session?.user.name ?? "").trim().split(/\s+/)[0];

  if (guide) {
    return <GettingStarted onDismiss={() => void navigate({ to: ROUTES.overview, search: {} })} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={name ? `Your queue, ${name}` : "Your queue"}
        description="Review alerts, continue investigations, and see what you are watching."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to={ROUTES.gettingStarted}>Getting started</Link>
            </Button>
            {access.canStartCase && (
              <Button asChild size="sm"><Link to={ROUTES.investigations}>Start an investigation</Link></Button>
            )}
          </div>
        }
      />
      <QuickStart />
      <section aria-label="Your work" className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <React.Suspense fallback={<p role="status" className="py-6 text-sm text-muted-foreground">Loading your queue…</p>}>
          <Queue key={identity} />
        </React.Suspense>
      </section>
    </div>
  );
}
